<template>
  <div
    class="min-h-screen flex items-center justify-center bg-white md:bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">

      <div class="flex justify-center">
        <div class="mb-10 w-5/12">
          <YogoIcon></YogoIcon>
        </div>
      </div>

      <div class="md:bg-white md:border rounded-md border-gray-200 py-8 md:py-10 sm:px-6 md:px-8">

        <h2 class="mt-6 text-center">
            <p>{{ $t('login.welcomeToYogo') }}</p>
        </h2>
        <h3 style="width: 300px; text-align: center; margin: auto; margin-top: 20px;">
          {{ $t('login.letsStartFreeDemo') }}
        </h3>

        <div class="message" v-show="loginErrorMessage">
          {{ loginErrorMessage }}
        </div>

        <form class="mt-8" novalidate @submit.prevent="submitForm">
            <InputField
                :label="$t('global.StudioName')"
                :class="getValidationClass('client_name')"
                type="text"
                v-model="form.client_name"
                :required="true"
            >
                <template v-slot:errors>
                  <span class="error" v-if="!$v.form.client_name.required">
                      {{ $t('login.studioNameMustBeEntered') }}
                  </span>
                </template>
            </InputField>

            <InputField
                :label="$t('global.FirstName')"
                :class="getValidationClass('first_name')"
                type="text"
                v-model="form.first_name"
                :required="true"
            >
                <template v-slot:errors>
                  <span class="error" v-if="!$v.form.first_name.required">
                      {{ $t('login.firstNameMustBeEntered') }}
                  </span>
                </template>
            </InputField>

            <InputField
                :label="$t('global.LastName')"
                :class="getValidationClass('last_name')"
                type="text"
                v-model="form.last_name"
                :required="true"
            >
                <template v-slot:errors>
                  <span class="error" v-if="!$v.form.last_name.required">
                      {{ $t('login.lastNameMustBeEntered') }}
                  </span>
                </template>
            </InputField>

          <InputField
              :label="urbanSportsClubContext ? $t('global.UrbanSportsClubProfileLink') : $t('global.Website')"
              :class="getValidationClass('website')"
              type="text"
              v-model="form.website"
              :required="urbanSportsClubContext"
          >
            <template v-slot:errors>
                  <span class="error" v-if="!$v.form.website.required">
                      {{ $t('global.IsRequired') }}
                  </span>
                  <span class="error" v-else-if="!$v.form.website.valid">
                      {{ $t('global.NeedsToBeAWebsite') }}
                  </span>
            </template>
          </InputField>

          <country-select
              :required="true"
              :country="form.country || defaultCountry"
              :label="$t('global.Country')"
              @update:country="handleCountryChange"
          />

            <InputField
                :label="$t('global.Email')"
                :class="getValidationClass('email')"
                type="email"
                v-model="form.email"
                autocomplete="email"
                :required="true"
            >
                <template v-slot:errors>
                  <span class="error" v-if="!$v.form.email.required">
                      {{ $t('login.emailMustBeEntered') }}
                  </span>
                  <span class="error"
                      v-else-if="!$v.form.email.email">{{ $t('login.notValidEmail') }}</span>
                </template>
            </InputField>

            <InputField
                :label="$t('global.Password')"
                :class="getValidationClass('password')"
                type="password"
                v-model="form.password"
                autocomplete="new-password"
                :required="true"
            >
                <template v-slot:errors>
                  <span class="error"
                      v-if="!$v.form.password.valid || !$v.form.password.minLength">
                    {{ $t('login.passDesc') }}
                  </span>
                </template>
            </InputField>

            <TermsCheckbox
              v-if="urbanSportsClubContext"
              v-model="form.acceptTrialTerms"
              :class="getValidationClass('acceptTrialTerms')"
              :label-text="$t('global.TrialAccessTermsAccept')"
              :link-text="$t('global.TrialAccessTermsLink')"
              :link="trialTermsUrl"
            >
              <template v-slot:errors>
                <span class="error" v-if="!$v.form.acceptTrialTerms.valid">
                      {{ $t('global.IsRequired') }}
                </span>
              </template>
            </TermsCheckbox>

            <YogoButton
              primary
              class="w-full"
              type="submit"
            >
              {{ $t('login.startMyFreeDemo') }}
              <clip-loader
                class="flex items-center"
                :loading="loading"
                :color="color"
                :size="size"
                v-bind:style="{marginLeft: '20px'}"
              ></clip-loader>
            </YogoButton>

        </form>

      </div>
    </div>

  </div>
</template>

<script>

import { validationMixin } from 'vuelidate';
import { required, email, minLength, requiredIf } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex';
import YogoApi from '@/gateways/YogoApi';
import Imgix from '@/services/Imgix';
import CountrySelect from '@/components/CountrySelect.vue';
import TermsCheckbox from '@/components/TermsCheckbox.vue';
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import InputField from './InputField.vue';
import YogoButton from './YogoButton.vue';
import YogoIcon from '../graphics/icons/YogoIcon.vue';

export default {
  mixins: [validationMixin, Imgix],
  components: {
    CountrySelect,
    TermsCheckbox,
    InputField,
    ClipLoader,
    YogoButton,
    YogoIcon,
  },
  data() {
    return {
      rememberMe: false,
      loginErrorMessage: '',
      loginFailed: false,
      form: {
        client_name: '',
        first_name: '',
        last_name: '',
        website: '',
        country: null,
        email: '',
        password: '',
        acceptTrialTerms: null,
      },

      color: '#ffffff',
      size: '15px',
      margin: '2px',
      radius: '2px',
      loading: false,

    };
  },
  computed: {
    ...mapGetters([
      'requestedRoute',
      'imageServer',
      'client',
    ]),
    ...mapGetters('appSettings', ['locale']),
    urbanSportsClubContext() {
      return !!this.$route.query.usc;
    },
    defaultCountry() {
      switch (this.locale) {
        case 'da':
          return 'DK';
        case 'sv':
          return 'SE';
        case 'nb':
          return 'NO';
        case 'pt':
          return 'PT';
        case 'de':
          return 'DE';
        default:
          return 'DK';
      }
    },
    trialTermsUrl() {
      if (this.locale === 'de') {
        return 'https://yogobooking.de/trial-account-terms';
      }
      return 'https://yogo.dk/trial-access-terms';
    },
  },

  methods: {
    handleCountryChange(country) {
      this.form.country = country;
    },
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName];

      if (field) {
        return {
          invalid: field.$invalid && field.$dirty,
        };
      }
      return {};
    },

    async submitForm() {
      this.$v.$touch();

      if (this.$v.$invalid) return;

      this.loading = true;

      const submitData = {
        ...this.form,
        locale: this.locale,
        is_usc_admin_signup: !!this.$route.query.usc,
      };

      const response = await YogoApi.post(`/client-signing-up?from=${window.location.origin}`, submitData);

      if (response.error) {
        this.loading = false;
        this.$notify({
          group: 'foo',
          title: 'Error',
          text: response.error,
          type: 'error',
        });
      } else {
        this.loading = false;
        this.$router.push({
          name: 'FirstStepConfirm',
          params: { },
        });
      }
    },
  },
  validations: {
    form: {
      client_name: {
        required,
      },
      first_name: {
        required,
      },
      last_name: {
        required,
      },
      email: {
        required,
        email,
      },
      website: {
        // eslint-disable-next-line func-names
        required: requiredIf(function () {
          return this.urbanSportsClubContext;
        }),
        valid(value) {
          if (!this.urbanSportsClubContext) {
            if (!value || value.length === 0) {
              return true;
            }
          }
          // eslint-disable-next-line no-useless-escape
          const websiteRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/i;
          return websiteRegex.test(value);
        },
      },
      password: {
        required,
        valid(value) {
          const containsUppercase = /[A-Z]/.test(value);
          const containsLowercase = /[a-z]/.test(value);
          const containsNumber = /[0-9]/.test(value);
          // const containsSpecial = /[#?!@$%^&*-]/.test(value)
          return containsUppercase && containsLowercase && containsNumber;
        },
        minLength: minLength(6),
      },
      acceptTrialTerms: {
        valid(value) {
          if (!this.urbanSportsClubContext) {
            return true;
          }
          return value === true;
        },
        // eslint-disable-next-line func-names
        required: requiredIf(function () {
          return this.urbanSportsClubContext;
        }),
      },
    },
  },


};

</script>

<style lang="stylus" scoped="true">

label
  display block
  height 100%

.router-link-active
  background none

input.error
  border-color: red;

</style>
